/**
 * Voice-limiting bookkeeping, independent of Web Audio so it can be unit
 * tested. The engine asks for a slot before building a voice; the pool
 * enforces a global voice cap, a per-sound instance cap and a minimum
 * retrigger interval (so an AoE hitting fifteen monsters in one tick does not
 * stack fifteen identical impacts). When full, the oldest voice whose
 * priority is not higher than the newcomer's is stolen.
 */

export interface VoiceRequest {
  /** Sound id (instance caps and retrigger intervals apply per key). */
  key: string;
  /** Current time in seconds. */
  now: number;
  /** Higher = more important. Voices are only stolen from equal or lower priority. */
  priority?: number;
  /** Max simultaneous voices of this key; the oldest is stolen beyond it. */
  maxInstances?: number;
  /** Requests closer than this to the previous start of the same key are rejected. */
  minInterval?: number;
}

export interface VoiceGrant {
  handle: number;
  /** Handles the caller must fade out and release (already removed from the pool). */
  stolen: number[];
}

interface Slot {
  handle: number;
  key: string;
  start: number;
  end: number;
  priority: number;
}

export class VoicePool {
  private voices: Slot[] = [];
  private lastStart = new Map<string, number>();
  private nextHandle = 1;

  constructor(public maxVoices = 40) {}

  get size(): number {
    return this.voices.length;
  }

  countOf(key: string): number {
    let n = 0;
    for (const v of this.voices) if (v.key === key) n++;
    return n;
  }

  has(handle: number): boolean {
    return this.voices.some((v) => v.handle === handle);
  }

  /** Active handles, oldest first. */
  handles(): number[] {
    return [...this.voices]
      .sort((a, b) => a.start - b.start || a.handle - b.handle)
      .map((v) => v.handle);
  }

  /**
   * Reserves a voice slot. Returns null when the request is rejected (too
   * soon after the same sound, or every slot is held by higher priority).
   * Nothing is mutated on rejection.
   */
  request(r: VoiceRequest): VoiceGrant | null {
    const priority = r.priority ?? 1;
    const maxInstances = r.maxInstances ?? Infinity;
    const minInterval = r.minInterval ?? 0;
    if (maxInstances <= 0 || this.maxVoices <= 0) return null;
    const last = this.lastStart.get(r.key);
    if (last !== undefined && minInterval > 0 && r.now - last < minInterval) return null;

    const oldestFirst = (a: Slot, b: Slot): number => a.start - b.start || a.handle - b.handle;
    const stolen = new Set<Slot>();
    // Per-key cap: the oldest instances of the same sound make way.
    const same = this.voices.filter((v) => v.key === r.key).sort(oldestFirst);
    for (let i = 0; i <= same.length - maxInstances; i++) stolen.add(same[i]!);
    // Global cap: steal the oldest voices that are not more important.
    let over = this.voices.length - stolen.size - (this.maxVoices - 1);
    if (over > 0) {
      const victims = this.voices
        .filter((v) => !stolen.has(v) && v.priority <= priority)
        .sort(oldestFirst);
      if (victims.length < over) return null;
      for (const v of victims) {
        if (over <= 0) break;
        stolen.add(v);
        over--;
      }
    }

    if (stolen.size) this.voices = this.voices.filter((v) => !stolen.has(v));
    const handle = this.nextHandle++;
    this.voices.push({ handle, key: r.key, start: r.now, end: Infinity, priority });
    this.lastStart.set(r.key, r.now);
    return { handle, stolen: [...stolen].map((v) => v.handle) };
  }

  /** Sets when a granted voice finishes on its own. */
  setEnd(handle: number, end: number): void {
    const v = this.voices.find((s) => s.handle === handle);
    if (v) v.end = end;
  }

  release(handle: number): boolean {
    const n = this.voices.length;
    this.voices = this.voices.filter((v) => v.handle !== handle);
    return this.voices.length !== n;
  }

  /** Removes and returns every voice whose end time has passed. */
  expire(now: number): number[] {
    if (!this.voices.some((v) => v.end <= now)) return [];
    const done: number[] = [];
    this.voices = this.voices.filter((v) => {
      if (v.end <= now) {
        done.push(v.handle);
        return false;
      }
      return true;
    });
    return done;
  }

  /** Drops every voice (returns their handles) and forgets retrigger history. */
  clear(): number[] {
    const all = this.voices.map((v) => v.handle);
    this.voices = [];
    this.lastStart.clear();
    return all;
  }
}
